import React, { useState, Children } from 'react';
import { ChevronUp, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type FooterPanel = 'privacy' | 'terms' | 'contact' | null;
export function Footer() {
  const [open, setOpen] = useState<FooterPanel>(null);
  const toggle = (panel: FooterPanel) => setOpen(open === panel ? null : panel);
  return <footer className="w-full bg-bg-footer py-6 px-4 sm:px-6 lg:px-8 relative" data-id="element-91">
      <div className="max-w-3xl mx-auto" data-id="element-92">
        <div className="text-center text-[13px] text-gray-300 font-bold tracking-wide mb-4" data-id="element-93">
          AirGuard Technologies
        </div>

        <div className="flex flex-wrap justify-center gap-3 text-[13px] text-gray-400 mb-4" data-id="element-94">
          <button onClick={() => toggle('privacy')} className="hover:text-white transition-colors flex items-center gap-1" data-id="element-95">
            Privacy Policy
            <ChevronUp className={`w-3 h-3 transition-transform duration-200 ${open === 'privacy' ? '' : 'rotate-180'}`} data-id="element-96" />
          </button>
          <span className="hidden sm:inline text-gray-600" data-id="element-97">|</span>
          <button onClick={() => toggle('terms')} className="hover:text-white transition-colors flex items-center gap-1" data-id="element-98">
            Terms of Service
            <ChevronUp className={`w-3 h-3 transition-transform duration-200 ${open === 'terms' ? '' : 'rotate-180'}`} data-id="element-99" />
          </button>
          <span className="hidden sm:inline text-gray-600" data-id="element-100">|</span>
          <button onClick={() => toggle('contact')} className="hover:text-white transition-colors flex items-center gap-1" data-id="element-101">
            Contact Us
            <ChevronUp className={`w-3 h-3 transition-transform duration-200 ${open === 'contact' ? '' : 'rotate-180'}`} data-id="element-102" />
          </button>
        </div>

        <div className="text-center text-[13px] text-gray-500" data-id="element-103">
          &copy; 2026 AirGuard Technologies. All rights reserved.
        </div>
      </div>

      {/* Bottom-sheet popup */}
      <AnimatePresence data-id="element-104">
        {open && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2
        }} onClick={() => setOpen(null)} className="fixed inset-0 bg-black/60 z-[60]" data-id="element-105" />
            <motion.div key={open} initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          duration: 0.35,
          ease: [0.32, 0.72, 0, 1]
        }} className="fixed bottom-0 left-0 right-0 z-[70] bg-[#1a2e4a] border-t border-white/10 rounded-t-2xl max-h-[75vh] flex flex-col" data-id="element-106">
              <div className="w-10 h-1 bg-gray-500 rounded-full mx-auto mt-2 mb-1" data-id="element-107" />
              <div className="flex items-center justify-between px-5 pb-2 border-b border-white/10 flex-shrink-0" data-id="element-108">
                <span className="text-white font-bold text-[14px]" data-id="element-109">
                  {open === 'privacy' && 'Privacy Policy'}
                  {open === 'terms' && 'Terms of Service'}
                  {open === 'contact' && 'Contact Us'}
                </span>
                <button onClick={() => setOpen(null)} className="text-gray-400 hover:text-white" data-id="element-110">
                  <X className="w-5 h-5" data-id="element-111" />
                </button>
              </div>

              <div className="overflow-y-auto p-5 text-gray-400 text-[12px] leading-[1.7]" data-id="element-112">
                {open === 'privacy' && <div className="space-y-4" data-id="element-113">
                    <p className="text-gray-500 text-[11px]" data-id="element-114">
                      Last updated: April 2026
                    </p>
                    <p data-id="element-115">
                      AirGuard Technologies (&ldquo;we,&rdquo; &ldquo;us,&rdquo;
                      or &ldquo;our&rdquo;) operates the website
                      www.pryvex.store and related services. This Privacy Policy
                      explains how we collect, use, disclose, and safeguard your
                      information when you visit our website and purchase our
                      products. Please read this policy carefully. By using our
                      website, you consent to the practices described herein.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-116">
                      1. Information We Collect
                    </h4>
                    <p data-id="element-117">
                      <strong className="text-gray-300" data-id="element-118">Personal Data:</strong>{' '}
                      When you place an order, create an account, or contact us,
                      we may collect personally identifiable information
                      including but not limited to: your full name, email
                      address, postal address, telephone number, payment
                      information (credit/debit card numbers processed securely
                      through Shopify Payments and third-party processors), and
                      shipping address.
                    </p>
                    <p data-id="element-119">
                      <strong className="text-gray-300" data-id="element-120">
                        Automatically Collected Data:
                      </strong>{' '}
                      When you access our website, we automatically collect
                      certain information about your device, including your IP
                      address, browser type, operating system, referring URLs,
                      pages viewed, time spent on pages, and other diagnostic
                      data. We use cookies, web beacons, tracking pixels
                      (including Meta Pixel), and similar technologies to
                      collect this information.
                    </p>
                    <p data-id="element-121">
                      <strong className="text-gray-300" data-id="element-122">Order Data:</strong> We
                      collect information related to your purchases, including
                      product(s) ordered, order value, date and time of
                      transaction, shipping preferences, and any communications
                      related to your order.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-123">
                      2. How We Use Your Information
                    </h4>
                    <p data-id="element-124">
                      We use the information we collect for the following
                      purposes:
                    </p>
                    <ul className="list-disc pl-5 space-y-1" data-id="element-125">
                      <li data-id="element-126">
                        To process and fulfill your orders, including shipping,
                        payment processing, and order confirmation
                        communications.
                      </li>
                      <li data-id="element-127">
                        To communicate with you regarding your account, orders,
                        and customer service inquiries.
                      </li>
                      <li data-id="element-128">
                        To send you marketing communications, promotional
                        offers, and product updates (you may opt out at any
                        time).
                      </li>
                      <li data-id="element-129">
                        To personalize your experience and deliver targeted
                        advertising through platforms such as Meta
                        (Facebook/Instagram) using tracking technologies.
                      </li>
                      <li data-id="element-130">
                        To improve our website, products, and services through
                        analytics and usage data.
                      </li>
                      <li data-id="element-131">
                        To detect, prevent, and address fraud, security issues,
                        and technical problems.
                      </li>
                      <li data-id="element-132">
                        To comply with legal obligations and enforce our terms
                        and policies.
                      </li>
                    </ul>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-133">
                      3. Cookies and Tracking Technologies
                    </h4>
                    <p data-id="element-134">
                      We use cookies and similar tracking technologies to track
                      activity on our website and store certain information.
                      These include:
                    </p>
                    <ul className="list-disc pl-5 space-y-1" data-id="element-135">
                      <li data-id="element-136">
                        <strong className="text-gray-300" data-id="element-137">
                          Essential Cookies:
                        </strong>{' '}
                        Required for the website to function properly (e.g.,
                        shopping cart, authentication).
                      </li>
                      <li data-id="element-138">
                        <strong className="text-gray-300" data-id="element-139">
                          Analytics Cookies:
                        </strong>{' '}
                        Help us understand how visitors interact with our
                        website (e.g., Google Analytics).
                      </li>
                      <li data-id="element-140">
                        <strong className="text-gray-300" data-id="element-141">
                          Advertising Cookies:
                        </strong>{' '}
                        Used to deliver relevant advertisements and track
                        campaign performance (e.g., Meta Pixel, Google Ads).
                      </li>
                    </ul>
                    <p data-id="element-142">
                      You can instruct your browser to refuse all cookies or
                      indicate when a cookie is being sent. However, some
                      features of our website may not function properly without
                      cookies.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-143">
                      4. Third-Party Sharing
                    </h4>
                    <p data-id="element-144">
                      We may share your information with the following
                      categories of third parties:
                    </p>
                    <ul className="list-disc pl-5 space-y-1" data-id="element-145">
                      <li data-id="element-146">
                        <strong className="text-gray-300" data-id="element-147">
                          Service Providers:
                        </strong>{' '}
                        Shopify (e-commerce platform), payment processors
                        (Stripe, PayPal, Klarna), shipping carriers, email
                        marketing platforms, and analytics providers.
                      </li>
                      <li data-id="element-148">
                        <strong className="text-gray-300" data-id="element-149">
                          Advertising Partners:
                        </strong>{' '}
                        Meta Platforms (Facebook/Instagram) for targeted
                        advertising and conversion tracking via Meta Pixel.
                      </li>
                      <li data-id="element-150">
                        <strong className="text-gray-300" data-id="element-151">
                          Legal Requirements:
                        </strong>{' '}
                        We may disclose your information if required by law,
                        regulation, legal process, or governmental request.
                      </li>
                    </ul>
                    <p data-id="element-152">
                      We do not sell your personal information to third parties.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-153">
                      5. Data Retention
                    </h4>
                    <p data-id="element-154">
                      We retain your personal data for as long as necessary to
                      fulfill the purposes outlined in this policy, comply with
                      legal obligations, resolve disputes, and enforce our
                      agreements. Order data is typically retained for a minimum
                      of 5 years for tax and accounting purposes.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-155">
                      6. Data Security
                    </h4>
                    <p data-id="element-156">
                      We implement industry-standard security measures to
                      protect your personal information, including SSL/TLS
                      encryption for all data transmissions, secure payment
                      processing through PCI-DSS compliant providers, and
                      restricted access to personal data within our
                      organization. However, no method of transmission over the
                      Internet is 100% secure, and we cannot guarantee absolute
                      security.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-157">
                      7. Your Rights
                    </h4>
                    <p data-id="element-158">
                      Depending on your jurisdiction, you may have the following
                      rights regarding your personal data:
                    </p>
                    <ul className="list-disc pl-5 space-y-1" data-id="element-159">
                      <li data-id="element-160">
                        <strong className="text-gray-300" data-id="element-161">Access:</strong>{' '}
                        Request a copy of the personal data we hold about you.
                      </li>
                      <li data-id="element-162">
                        <strong className="text-gray-300" data-id="element-163">
                          Rectification:
                        </strong>{' '}
                        Request correction of inaccurate or incomplete data.
                      </li>
                      <li data-id="element-164">
                        <strong className="text-gray-300" data-id="element-165">Erasure:</strong>{' '}
                        Request deletion of your personal data (subject to legal
                        retention requirements).
                      </li>
                      <li data-id="element-166">
                        <strong className="text-gray-300" data-id="element-167">Portability:</strong>{' '}
                        Request transfer of your data to another service
                        provider.
                      </li>
                      <li data-id="element-168">
                        <strong className="text-gray-300" data-id="element-169">Opt-Out:</strong>{' '}
                        Unsubscribe from marketing communications at any time
                        via the link in our emails.
                      </li>
                    </ul>
                    <p data-id="element-170">
                      To exercise any of these rights, contact us at
                      support@airguard-tech.com.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-171">
                      8. International Transfers
                    </h4>
                    <p data-id="element-172">
                      Your information may be transferred to and processed in
                      countries other than your country of residence. We ensure
                      appropriate safeguards are in place to protect your data
                      in accordance with applicable data protection laws,
                      including GDPR for European users and CCPA for California
                      residents.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-173">
                      9. Children&apos;s Privacy
                    </h4>
                    <p data-id="element-174">
                      Our website and services are not directed to individuals
                      under the age of 16. We do not knowingly collect personal
                      information from children. If we become aware that we have
                      collected data from a child without parental consent, we
                      will take steps to delete that information promptly.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-175">
                      10. Changes to This Policy
                    </h4>
                    <p data-id="element-176">
                      We reserve the right to update this Privacy Policy at any
                      time. Changes will be posted on this page with an updated
                      &ldquo;Last updated&rdquo; date. Your continued use of our
                      website after any changes constitutes acceptance of the
                      revised policy.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-177">
                      11. Contact Us
                    </h4>
                    <p data-id="element-178">
                      If you have questions or concerns about this Privacy
                      Policy, please contact us at:
                      <br data-id="element-179" />
                      support@airguard-tech.com — Response within 24 hours
                    </p>
                  </div>}

                {open === 'terms' && <div className="space-y-4" data-id="element-180">
                    <p className="text-gray-500 text-[11px]" data-id="element-181">
                      Last updated: April 2026
                    </p>
                    <p data-id="element-182">
                      These Terms of Service (&ldquo;Terms&rdquo;) govern your
                      access to and use of the AirGuard Technologies website
                      (www.pryvex.store) and the purchase of our products. By
                      accessing our website or placing an order, you agree to be
                      bound by these Terms.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-183">
                      1. Products and Descriptions
                    </h4>
                    <p data-id="element-184">
                      We make every effort to display our products and their
                      features as accurately as possible. However, we do not
                      guarantee that product descriptions, images, pricing, or
                      other content on our website are error-free, complete, or
                      current. We reserve the right to correct any errors and to
                      change or update information at any time without prior
                      notice.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-185">
                      2. Orders and Payment
                    </h4>
                    <p data-id="element-186">
                      By placing an order, you represent that you are at least
                      18 years of age and that the information you provide is
                      accurate and complete. We reserve the right to refuse or
                      cancel any order for any reason, including suspected
                      fraud, unauthorized transactions, or pricing errors. All
                      payments are processed securely through Shopify Payments
                      and third-party processors (Stripe, PayPal, Klarna).
                      Prices are listed in USD unless otherwise stated.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-187">
                      3. Shipping and Delivery
                    </h4>
                    <p data-id="element-188">
                      We offer free worldwide shipping on all orders. Orders are
                      dispatched within 24 hours of purchase. Estimated delivery
                      times are 3-5 business days for US orders and 7-14
                      business days for international orders. Delivery times are
                      estimates and not guaranteed. We are not responsible for
                      delays caused by customs, weather, carrier issues, or
                      other circumstances beyond our control.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-189">
                      4. 30-Day Money-Back Guarantee
                    </h4>
                    <p data-id="element-190">
                      We offer a 30-day money-back guarantee on all purchases.
                      If you are not satisfied with your AirGuard Pro for any
                      reason, you may return it within 30 days of delivery for a
                      full refund. To initiate a return, contact us at
                      support@airguard-tech.com. Items must be returned in their
                      original packaging and in reasonable condition. Refunds
                      will be processed within 5-7 business days of receiving
                      the returned item.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-191">
                      5. 3-Year Warranty
                    </h4>
                    <p data-id="element-192">
                      All AirGuard Pro devices are covered by a 3-year
                      manufacturer warranty from the date of purchase. This
                      warranty covers defects in materials and workmanship under
                      normal use. If your device experiences a covered defect,
                      we will replace it free of charge. The warranty does not
                      cover damage caused by misuse, unauthorized modification,
                      accidents, or normal wear and tear.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-193">
                      6. Intellectual Property
                    </h4>
                    <p data-id="element-194">
                      All content on our website, including text, graphics,
                      logos, images, product designs, and software, is the
                      property of AirGuard Technologies and is protected by
                      international copyright, trademark, and intellectual
                      property laws. You may not reproduce, distribute, modify,
                      or create derivative works from any content without our
                      prior written consent.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-195">
                      7. Limitation of Liability
                    </h4>
                    <p data-id="element-196">
                      To the fullest extent permitted by law, AirGuard
                      Technologies shall not be liable for any indirect,
                      incidental, special, consequential, or punitive damages
                      arising from your use of our website or products. AirGuard
                      Pro is a consumer-grade CO monitoring device and is not
                      intended to replace professional CO detection systems
                      required by local building codes.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-197">
                      8. Governing Law
                    </h4>
                    <p data-id="element-198">
                      These Terms shall be governed by and construed in
                      accordance with the laws of the State of Delaware, United
                      States, without regard to conflict of law principles.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-199">
                      9. Changes to Terms
                    </h4>
                    <p data-id="element-200">
                      We reserve the right to modify these Terms at any time.
                      Changes will be effective immediately upon posting. Your
                      continued use of our website constitutes acceptance of the
                      updated Terms.
                    </p>

                    <h4 className="text-gray-300 font-semibold text-[13px] mt-3" data-id="element-201">
                      10. Contact
                    </h4>
                    <p data-id="element-202">
                      For questions about these Terms, contact us at:
                      <br data-id="element-203" />
                      support@airguard-tech.com — Response within 24 hours
                    </p>
                  </div>}

                {open === 'contact' && <div className="space-y-4" data-id="element-204">
                    <p data-id="element-205">
                      We&apos;re here to help. If you have any questions about
                      your order, our products, warranty claims, or anything
                      else, don&apos;t hesitate to reach out.
                    </p>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4 mt-3" data-id="element-206">
                      <p className="text-gray-300 font-semibold text-[14px] mb-2" data-id="element-207">
                        Email Support
                      </p>
                      <p className="text-white font-bold" data-id="element-208">
                        support@airguard-tech.com
                      </p>
                      <p className="text-gray-400 text-[12px] mt-1" data-id="element-209">
                        Response within 24 hours
                      </p>
                    </div>
                    <p className="mt-3" data-id="element-210">
                      Our support team is available Monday through Friday, 9:00
                      AM — 6:00 PM EST. We typically respond to all inquiries
                      within 24 hours. For warranty claims, please have your
                      order number ready.
                    </p>
                  </div>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </footer>;
}